<template>
    <div class="table-responsive">
        <loader v-if="isLoading" />
        <b-table-simple v-else borderless hover class="list">
            <b-thead>
                <b-tr>
                    <b-th
                        v-if="estimateActions.length > 0 && isEditable"
                    ></b-th>
                    <b-th>Action:</b-th>
                    <b-th>When Using:</b-th>
                    <b-th>For:</b-th>
                    <b-th>Value:</b-th>
                    <b-th>Note:</b-th>
                    <b-th>Created By:</b-th>
                    <b-th>Added:</b-th>
                    <b-th></b-th>
                </b-tr>
            </b-thead>
            <b-tbody v-if="estimateActions.length === 0">
                <b-tr>
                    <td colspan="8">
                        <i>No records available</i>
                    </td>
                </b-tr>
            </b-tbody>
            <draggable
                v-else
                v-model="estimateActions"
                tag="tbody"
                ghost-class="sort-row-ghost"
                drag-class="sort-row-drag"
                :animation="0"
                handle=".handle"
                @start="isRowDragging = true"
                @end="isRowDragging = false"
                @sort="itemSorted"
            >
                <tr v-for="action in estimateActions" :key="action.id">
                    <td v-if="isEditable" class="handle">
                        <i class="cursor-move cus-black-white-swatch" />
                    </td>
                    <td>
                        <span v-if="action.action_type">{{
                            action.action_type.type
                        }}</span>
                    </td>
                    <td>
                        <span>{{ action.itemName }}</span>
                    </td>
                    <td>
                        <span>{{ action.createFor }}</span>
                    </td>
                    <td>
                        <template
                            v-if="
                                checkActionType(
                                    action,
                                    ACTIONS.ID.CREATE_LETTER
                                )
                            "
                        >
                            <letter-link
                                v-for="(val, i) in action.value"
                                :key="i"
                                :letter="val"
                                :text="val.title"
                                :index="i"
                                target="_blank"
                                :length="action.value.length"
                            />
                        </template>
                        <template
                            v-else-if="
                                checkActionType(action, ACTIONS.ID.CREATE_FORM)
                            "
                        >
                            <span v-for="(val, i) in action.value" :key="i">
                                <webform-link
                                    router-name="template_details"
                                    :webform="val"
                                    :text="val.title"
                                    target="_blank"
                                    :withComma="i != action.value.length - 1"
                                />
                            </span>
                        </template>

                        <template
                            v-else-if="
                                checkActionType(
                                    action,
                                    ACTIONS.ID.ADD_ATTACHMENT
                                )
                            "
                        >
                            <span
                                v-for="(val, index) in action.value"
                                :key="index"
                            >
                                <a
                                    v-if="val.file_url"
                                    :href="val.file_url ? val.file_url : ''"
                                    target="_blank"
                                    >{{ val.title }}</a
                                >
                                <span v-else>{{ val.title }}</span>

                                <span v-if="index != action.value.length - 1"
                                    >, &nbsp;</span
                                >
                            </span>
                        </template>
                        <template v-else>
                            <span v-html="action.value"></span>
                        </template>
                    </td>
                    <td class="text-nowrap">
                        <span v-html="action.note"></span>
                    </td>
                    <td>
                        {{ action.createdBy }}
                    </td>
                    <td>
                        {{ _carbonDateDisplay(action.create_at) }}
                    </td>
                    <td class="text-right small text-nowrap">
                        <span
                            v-if="isEditable"
                            title="Edit Action"
                            @click="editAction(action)"
                            class="cursor-pointer"
                            >Edit</span
                        >
                        <span v-if="isEditable && isDeletable"> | </span>
                        <span
                            v-if="isDeletable"
                            title="Delete Action"
                            @click="deleteAction(action)"
                            class="cursor-pointer"
                        >
                            Delete
                        </span>
                    </td>
                </tr>
            </draggable>
        </b-table-simple>

        <modal-delete-action ref="modalDeleteAction" @reload="getActions" />
    </div>
</template>

<script>
/**
 * @name TemplateEstimateActionList
 * @description List of template Estimate Action
 */

import TemplateService from "@/services/templates";
import ACTIONS from "@/constants/actions";
import TEMPLATES from "../../../constants/templates";
import LetterLink from "@/components/commons/letter-link";
import WebformLink from "@/components/commons/webform-link";
import ModalDeleteAction from "@/components/setting-template/bundle-invoice/modal/delete-action";
import { isArray, isObject } from "lodash";

export default {
    mixins: [],
    components: {
        Loader: () => import("@/components/elements/loader-list"),
        draggable: () => import("vuedraggable"),
        LetterLink,
        WebformLink,
        ModalDeleteAction
    },
    props: {
        estimate: {
            type: Object,
            required: false
        }
    },
    computed: {
        templateId() {
            return this.$store.state.settingsTemplate.templateId;
        },
        isEditable() {
            return this._hasAccess("inventory.item.action.update");
        },
        isDeletable() {
            return this._hasAccess("inventory.item.action.delete");
        }
    },
    watch: {},
    data() {
        return {
            isLoading: false,
            showPagination: false,
            estimateActions: [],
            isRowDragging: false,
            ACTIONS,
            TEMPLATES
        };
    },
    mounted() {
        this.getActions();
    },
    destroyed() {},
    methods: {
        async getActions() {
            try {
                this.isLoading = true;

                const res = await TemplateService.getActions(this.templateId);

                if (res.code === 200) {
                    this.estimateActions = res.resources;

                    this.estimateActions = this.estimateActions.map(action => {
                        if (isObject(action.value)) {
                            action.value = Object.values(action.value);
                        }

                        return action;
                    });
                } else {
                    throw new Error("Error: Unable to get actions");
                }
            } catch (error) {
                console.log(error);
                this._message("Error: Unable to get actions", "danger");
            } finally {
                this.$emit("reload-tab-count");
                this.isLoading = false;
            }
        },
        editAction(action) {
            this.$emit("update-action", action);
        },
        deleteAction(action) {
            this.$refs.modalDeleteAction.open(this.templateId, action);
        },
        async itemSorted() {
            try {
                let actionOrder = [];

                for (let n = 0; n < this.estimateActions.length; n++) {
                    let id = this.estimateActions[n].id;
                    actionOrder.push(id);
                }

                await TemplateService.updateInvoiceEstimateTemplateActionsOrder(
                    this.templateId,
                    {
                        is_action_item: 0,
                        action_items: actionOrder
                    }
                );
            } catch (error) {
                throw new Error(
                    `Error: Cannot sort the selected action, ${error}`
                );
            }
        },
        checkActionType(action, actionTypeToCheck) {
            return (
                action.action_type &&
                isArray(action.value) &&
                action.action_type.id == actionTypeToCheck
            );
        }
    }
};
</script>
